import {SliceStatus, StreamedSuperCoachChatMessage} from 'interfaces';
import {v4 as uuid} from 'uuid';

import {ActionReducerMapBuilder, createSlice} from '@reduxjs/toolkit';

import {
  endSuperCoachChatMessageStreamChunk,
  getSuperCoachChatMessages,
  getSuperCoachChatMessagesFailure,
  getSuperCoachChatMessagesSuccess,
  getSuperCoachRecommendations,
  getSuperCoachRecommendationsFailure,
  getSuperCoachRecommendationsSuccess,
  receiveSuperCoachChatMessageStreamChunk,
  saveSuperCoachActionFeedback,
  saveSuperCoachActionFeedbackFailure,
  saveSuperCoachActionFeedbackSuccess,
  sendSuperCoachChatMessage,
  sendSuperCoachChatMessageFailure,
  sendSuperCoachChatMessageSuccess,
  sendSuperCoachOnboardingData,
  sendSuperCoachOnboardingDataFailure,
  sendSuperCoachOnboardingDataSuccess,
  setOnCoachScreen,
  toggleSuperCoachIsStreaming,
} from './superCoachActions';

type SuperCoachSliceState = {
  messages: StreamedSuperCoachChatMessage[];
  isOnCoachScreen?: boolean;
  pagination: {
    page: number;
    nextPage: number | null;
    hasMore: boolean;
  };
  getMessagesStatus: SliceStatus;
  sendChatMessageStatus: SliceStatus;
  onboarded: boolean;
  sendSuperCoachDataStatus: SliceStatus;
  saveActionFeedbackStatus?: SliceStatus;
  getRecommendationsStatus?: SliceStatus;
  error: string | null;
};

export const superCoachInitialState: SuperCoachSliceState = {
  messages: [],
  pagination: {
    page: 0,
    nextPage: null,
    hasMore: false,
  },
  getMessagesStatus: SliceStatus.idle,
  sendChatMessageStatus: SliceStatus.idle,
  onboarded: false,
  sendSuperCoachDataStatus: SliceStatus.idle,
  saveActionFeedbackStatus: SliceStatus.idle,
  getRecommendationsStatus: SliceStatus.idle,
  error: null,
};

const superCoachSlice = createSlice({
  name: 'memberSuperCoach',
  initialState: superCoachInitialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<SuperCoachSliceState>) =>
    builder
      .addCase(setOnCoachScreen, (state, {payload}) => {
        state.isOnCoachScreen = payload;
      })
      .addCase(sendSuperCoachOnboardingData, state => {
        state.sendSuperCoachDataStatus = SliceStatus.pending;
        state.error = null;
      })
      .addCase(sendSuperCoachOnboardingDataFailure, (state, {payload}) => {
        state.sendSuperCoachDataStatus = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(sendSuperCoachOnboardingDataSuccess, state => {
        state.onboarded = true;
        state.sendSuperCoachDataStatus = SliceStatus.resolved;
        state.error = null;
      })
      /**
       * Save SuperCoach action feedback.
       */
      .addCase(saveSuperCoachActionFeedback, (state, {payload}) => {
        const {messageId, feedback, actionId} = payload;
        const msg = state.messages.find(m => m._id === messageId);

        if (msg && msg.actionRecommendations) {
          const rec = actionId
            ? msg.actionRecommendations.find(r => r._id === actionId)
            : msg.actionRecommendations[0];
          if (rec) {
            rec.feedback = feedback;
          }
        }

        state.saveActionFeedbackStatus = SliceStatus.pending;
        state.error = null;
      })
      .addCase(saveSuperCoachActionFeedbackFailure, (state, {payload}) => {
        state.saveActionFeedbackStatus = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(saveSuperCoachActionFeedbackSuccess, state => {
        state.saveActionFeedbackStatus = SliceStatus.resolved;
        state.error = null;
      })
      /**
       * Get SuperCoach recommendations.
       */
      .addCase(getSuperCoachRecommendations, state => {
        state.getRecommendationsStatus = SliceStatus.pending;
        state.error = '';
      })
      .addCase(getSuperCoachRecommendationsSuccess, (state, {payload}) => {
        if (payload) {
          const index = state.messages.findIndex(
            msg => msg._id === payload?._id,
          );
          // Use type assertion to StreamedSuperCoachChatMessage
          const newMessage = payload as StreamedSuperCoachChatMessage;
          newMessage.isStreaming = true;
          newMessage.isAutoEngagement = true;

          if (index > -1) {
            state.messages[index] = newMessage;
          } else {
            state.messages.push(newMessage); // Use push instead of creating a new array
          }
        }

        state.getRecommendationsStatus = SliceStatus.resolved;
        state.error = null;
      })
      .addCase(getSuperCoachRecommendationsFailure, (state, {payload}) => {
        state.getRecommendationsStatus = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(toggleSuperCoachIsStreaming, (state, {payload}) => {
        if (payload) {
          const index = state.messages.findIndex(
            item => item._id === payload._id,
          );
          if (index > -1) {
            // Copy all properties first
            Object.assign(state.messages[index], payload);
            // Then set isStreaming to false
            state.messages[index].isStreaming = false;
          }
        }
      })
      /**
       * Fetch SuperCoach chat messages.
       */
      .addCase(getSuperCoachChatMessages, (state, {payload}) => {
        state.getMessagesStatus = SliceStatus.pending;
        if (payload.reset) {
          state.messages = [];
        }
        state.error = null;
      })
      .addCase(getSuperCoachChatMessagesFailure, (state, {payload}) => {
        state.getMessagesStatus = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(getSuperCoachChatMessagesSuccess, (state, {payload}) => {
        state.messages = [...payload.items, ...state.messages];
        state.pagination = {
          hasMore: payload.hasMore,
          page: payload.page,
          nextPage: payload.nextPage,
        };
        state.getMessagesStatus = SliceStatus.resolved;
        state.error = null;
      })
      /**
       * Send a new SuperCoach chat message.
       */
      .addCase(sendSuperCoachChatMessage, (state, {payload}) => {
        state.messages.push({
          _id: `${uuid()}`,
          question: payload?.message ?? '',
          response: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isStreaming: true,
        });
        state.sendChatMessageStatus = SliceStatus.pending;
        state.error = null;
      })
      .addCase(receiveSuperCoachChatMessageStreamChunk, (state, {payload}) => {
        const index = state.messages.findIndex(item => item.isStreaming);
        if (index !== -1) {
          const message = state.messages[index];
          message.response = message.response + (payload.text ?? '');
          state.messages[index] = message;
        }
      })
      .addCase(endSuperCoachChatMessageStreamChunk, (state, {payload}) => {
        const index = state.messages.findIndex(
          item => item.isStreaming && !item.isAutoEngagement,
        );

        if (index !== -1) {
          // Create a copy of the message with updated properties
          const message = {
            ...state.messages[index], // Keep this spread as we're creating a new object
            ...(payload.message ?? {}),
          };
          const isActionOrContent =
            !!payload.message?.actionRecommendations?.length ||
            !!payload.message?.contentRecommendations?.length;
          message.isStreaming = isActionOrContent;
          state.messages[index] = message;

          if (payload.recommendedMessage) {
            state.messages.push({
              ...payload.recommendedMessage,
              isStreaming: true,
            });
          }

          state.sendChatMessageStatus = SliceStatus.resolved;
        }
      })
      .addCase(sendSuperCoachChatMessageSuccess, (state, {payload}) => {
        state.sendChatMessageStatus = SliceStatus.resolved;
        state.messages[state.messages.length - 1] = {...payload};
      })
      .addCase(sendSuperCoachChatMessageFailure, (state, {payload}) => {
        state.sendChatMessageStatus = SliceStatus.rejected;
        state.error = payload;
      }),
});

export const {reducer: superCoachReducer, name: superCoachReducerName} =
  superCoachSlice;

export type TSuperCoachActions =
  | ReturnType<typeof getSuperCoachChatMessages>
  | ReturnType<typeof getSuperCoachChatMessagesFailure>
  | ReturnType<typeof getSuperCoachChatMessagesSuccess>
  | ReturnType<typeof sendSuperCoachOnboardingData>
  | ReturnType<typeof sendSuperCoachOnboardingDataFailure>
  | ReturnType<typeof sendSuperCoachOnboardingDataSuccess>
  | ReturnType<typeof saveSuperCoachActionFeedback>
  | ReturnType<typeof saveSuperCoachActionFeedbackFailure>
  | ReturnType<typeof saveSuperCoachActionFeedbackSuccess>
  | ReturnType<typeof getSuperCoachRecommendations>
  | ReturnType<typeof getSuperCoachRecommendationsFailure>
  | ReturnType<typeof getSuperCoachRecommendationsSuccess>
  | ReturnType<typeof sendSuperCoachChatMessage>
  | ReturnType<typeof endSuperCoachChatMessageStreamChunk>
  | ReturnType<typeof receiveSuperCoachChatMessageStreamChunk>
  | ReturnType<typeof sendSuperCoachChatMessageFailure>
  | ReturnType<typeof sendSuperCoachChatMessageSuccess>
  | ReturnType<typeof toggleSuperCoachIsStreaming>
  | ReturnType<typeof setOnCoachScreen>;

export const superCoachActions = {
  getSuperCoachChatMessages,
  getSuperCoachChatMessagesFailure,
  getSuperCoachChatMessagesSuccess,
  sendSuperCoachOnboardingData,
  sendSuperCoachOnboardingDataFailure,
  sendSuperCoachOnboardingDataSuccess,
  saveSuperCoachActionFeedback,
  saveSuperCoachActionFeedbackFailure,
  saveSuperCoachActionFeedbackSuccess,
  getSuperCoachRecommendations,
  getSuperCoachRecommendationsFailure,
  getSuperCoachRecommendationsSuccess,
  sendSuperCoachChatMessage,
  endSuperCoachChatMessageStreamChunk,
  receiveSuperCoachChatMessageStreamChunk,
  sendSuperCoachChatMessageFailure,
  sendSuperCoachChatMessageSuccess,
  toggleSuperCoachIsStreaming,
  setOnCoachScreen,
};

export type SuperCoachState = ReturnType<typeof superCoachReducer>;
